import React from 'react';
import { Target, Award, Zap, Shield } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(40px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
    }}>
      {children}
    </div>
  );
};

const AboutPage = () => {
  return (
    <div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>

      {/* Hero */}
      <section className="relative py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1563453392212-326f5e854473?w=1600&q=80')`,
          backgroundSize: 'cover', backgroundPosition: 'center'
        }}/>
        <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
        <div className="relative z-10 text-center text-white px-4">
          <p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>OM OSS</p>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Vi är <span style={{ color: '#C9A84C' }}>GALAX</span></h1>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Motalas första elite städteam som kombinerar militär precision med sjukhusstandard.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <AnimatedSection>
              <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80"
                alt="Our story" className="rounded-2xl shadow-2xl w-full object-cover" style={{ height: '450px' }}/>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VÅR HISTORIA</p>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Hur Allt Började</h2>
              <div className="space-y-4" style={{ color: '#555' }}>
                <p>GALAX grundades med en enkel vision: att revolutionera städbranschen i Motala. Vi såg en marknad dominerad av monopol där kvalitet ofta sattes i andra hand.</p>
                <p>Vi bestämde oss för att göra något helt annorlunda. Istället för att skicka okvalificerad personal rekryterar vi städare med erfarenhet från högt reglerade miljöer — sjukhus och skolor — där precision inte är ett val, utan ett krav.</p>
                <p>Varje teammedlem genomgår vår träning (vid behov upp till 6 veckor) där de lär sig arbeta som ett synkroniserat team — precis som ett Formel 1-team i depån.</p>
              </div>
              <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
                <p className="font-bold" style={{ color: '#C9A84C' }}>Resultatet? Motalas första verkliga premium städtjänst.</p>
              </div>
            </AnimatedSection>
          </div>

          {/* Values */}
          <AnimatedSection className="text-center mb-12">
            <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VÅRA VÄRDERINGAR</p>
            <h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Vad Vi Står För</h2>
            <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
            {[
              { icon: <Target className="w-8 h-8"/>, title: 'Precision', desc: 'Varje detalj räknas. Vi lämnar inget åt slumpen.' },
              { icon: <Award className="w-8 h-8"/>, title: 'Excellence', desc: 'Bra är inte tillräckligt. Vi siktar alltid mot perfektion.' },
              { icon: <Zap className="w-8 h-8"/>, title: 'Effektivitet', desc: 'Vi slösar inte din tid. Snabbt OCH kvalitet.' },
              { icon: <Shield className="w-8 h-8"/>, title: 'Integritet', desc: 'Transparenta priser. Inga dolda avgifter.' }
            ].map((value, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.1}>
                <div className="p-8 rounded-2xl text-center h-full transition-all hover:scale-105"
                  style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#1a1a2e' }}>
                    <div style={{ color: '#C9A84C' }}>{value.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#1a1a2e' }}>{value.title}</h3>
                  <p style={{ color: '#888' }}>{value.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Why Different */}
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <AnimatedSection delay={0.1}>
              <p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VARFÖR VI ÄR ANNORLUNDA</p>
              <h2 className="text-4xl font-bold mb-8" style={{ color: '#1a1a2e' }}>Det Som Skiljer Oss Åt</h2>
              <div className="space-y-6">
                {[
                  { title: 'Sjukhus & Skol-Erfarenhet', desc: 'Våra städare har arbetat där misstag kan sprida infektioner. Den standarden tar vi med oss.' },
                  { title: 'Intensiv Träning (Vid Behov)', desc: 'Andra städfirmor ger 1-2 dagars introduktion. Vi tränar vårt team grundligt innan de städar sitt första hem.' },
                  { title: 'Team-Synkronisering', desc: 'Vi arbetar aldrig ensamma. Vårt team rör sig som en enhet — effektivare och snabbare.' },
                  { title: 'Transparenta Priser', desc: 'Vi visar exakt vad det kostar per kvadratmeter. Inga dolda avgifter, inga överraskningar.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start space-x-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ backgroundColor: '#C9A84C' }}>
                      <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                        <path d="M1 5L4.5 8.5L11 1" stroke="#1a1a2e" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{item.title}</h4>
                      <p style={{ color: '#666' }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80"
                alt="Quality cleaning" className="rounded-2xl shadow-2xl w-full object-cover" style={{ height: '500px' }}/>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;